/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * WrapUpPanel.tsx — locale-aware post-call completion workflow.
 * The panel validates the selected outcome and required scenario fields before
 * it emits the wrap-up command and returns the operator to the queue. It does
 * not persist records directly or own the realtime transport lifecycle.
 */

import { useCall, useWorkspace, useOperatorStore } from '@/store';
import { getWrapUpCompleteness } from '@/store/slices/workspaceSlice';
import { Panel, Button, Input } from '@/components/ui';
import { HighlightBox } from '@/features/copilot';
import { SCENARIO_FIELD_I18N_KEYS } from './customerCardTypes';
import type { WrapUpOutcome } from '@/store';
import { buildWrapUpComplete } from '@/realtime/commands';
import { useTranslation } from '@/i18n';

const OUTCOME_LABEL_KEYS: Record<WrapUpOutcome, string> = {
  meeting_scheduled: 'wrap_up.outcome.meeting_scheduled',
  callback: 'wrap_up.outcome.callback',
  resolved: 'wrap_up.outcome.resolved',
  no_answer: 'wrap_up.outcome.no_answer',
  other: 'wrap_up.outcome.other',
};

const OUTCOME_OPTIONS: WrapUpOutcome[] = [
  'meeting_scheduled',
  'callback',
  'resolved',
  'no_answer',
  'other',
];

/**
 * E4-S3: Wrap-up panel (Screen 07). Shown after call ended.
 * Outcome select, required fields checklist, notes, "Save and return to queue".
 */
export function WrapUpPanel() {
  const t = useTranslation();
  const call = useCall();
  const workspace = useWorkspace();
  const send = useOperatorStore((s) => s.send);
  const setWrapUpOutcome = useOperatorStore((s) => s.setWrapUpOutcome);
  const setScenarioField = useOperatorStore((s) => s.setScenarioField);
  const dismissWrapUp = useOperatorStore((s) => s.dismissWrapUp);

  const completeness = getWrapUpCompleteness(workspace);
  const outcome = workspace.wrapUpOutcome;
  const notesValue = workspace.scenarioFields.notes?.value ?? '';

  const canSave = outcome != null && completeness.complete && call.callId != null;

  const handleSaveAndReturn = () => {
    if (!canSave || !call.callId) return;
    const requiredFieldsStatus: Record<string, boolean> = {};
    for (const fieldId of workspace.wrapUpRequiredFields) {
      const v = workspace.scenarioFields[fieldId]?.value?.trim() ?? '';
      requiredFieldsStatus[fieldId] = v !== '';
    }
    const envelope = buildWrapUpComplete({
      call_id: call.callId,
      outcome,
      required_fields_status: requiredFieldsStatus,
      notes: notesValue.trim() || undefined,
    });
    send(envelope);
    dismissWrapUp();
  };

  if (call.phase !== 'ended') {
    return null;
  }

  return (
    <HighlightBox widgetId="wrap_up_panel">
      <Panel title={t('wrap_up.title')} padding="md">
        <div className="space-y-4">
          {call.endReason && (
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              {t('wrap_up.end_reason', { reason: call.endReason })}
              {call.durationMs != null && (
                <span className="ml-2">
                  · {t('wrap_up.duration', { duration: formatDuration(call.durationMs) })}
                </span>
              )}
            </p>
          )}

          <div>
            <label
              htmlFor="wrap-up-outcome"
              className="block text-sm font-medium mb-1"
              style={{ color: 'var(--text-secondary)' }}
            >
              {t('wrap_up.outcome')}
            </label>
            <select
              id="wrap-up-outcome"
              value={outcome ?? ''}
              onChange={(e) => setWrapUpOutcome((e.target.value as WrapUpOutcome) || null)}
              className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{
                borderColor: 'var(--glass-border)',
                background: 'var(--toggle-bg)',
                color: 'var(--text-primary)',
              }}
              aria-required
            >
              <option value="">— {t('wrap_up.select_outcome')} —</option>
              {OUTCOME_OPTIONS.map((o) => (
                <option key={o} value={o}>
                  {t(OUTCOME_LABEL_KEYS[o])}
                </option>
              ))}
            </select>
          </div>

          {workspace.wrapUpRequiredFields.length > 0 && (
            <div>
              <span
                className="block text-sm font-medium mb-2"
                style={{ color: 'var(--text-secondary)' }}
              >
                {t('wrap_up.required_fields')}
              </span>
              <ul className="space-y-1.5" role="list">
                {workspace.wrapUpRequiredFields.map((fieldId) => {
                  const filled = completeness.filled.includes(fieldId);
                  const label = t(SCENARIO_FIELD_I18N_KEYS[fieldId] ?? fieldId);
                  return (
                    <li key={fieldId} className="flex items-center gap-2 text-sm">
                      <span
                        className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs"
                        aria-hidden
                        title={filled ? t('wrap_up.filled') : t('wrap_up.not_filled')}
                      >
                        {filled ? (
                          <span className="text-green-600 dark:text-green-400" aria-hidden>
                            ✓
                          </span>
                        ) : (
                          <span className="text-amber-600 dark:text-amber-400" aria-hidden>
                            ○
                          </span>
                        )}
                      </span>
                      <span
                        style={{ color: filled ? 'var(--text-secondary)' : 'var(--text-muted)' }}
                      >
                        {label}
                      </span>
                    </li>
                  );
                })}
              </ul>
              {!completeness.complete && (
                <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">
                  {t('wrap_up.complete_required')}
                </p>
              )}
            </div>
          )}

          <div>
            <label
              htmlFor="wrap-up-notes"
              className="block text-sm font-medium mb-1"
              style={{ color: 'var(--text-secondary)' }}
            >
              {t('wrap_up.notes')}
            </label>
            <Input
              id="wrap-up-notes"
              value={notesValue}
              onChange={(e) => setScenarioField('notes', e.target.value, 'manual')}
              placeholder={t('wrap_up.notes_placeholder')}
              inputClassName="text-sm"
            />
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              variant="primary"
              onClick={handleSaveAndReturn}
              disabled={!canSave}
              aria-describedby={!canSave ? 'wrap-up-save-hint' : undefined}
            >
              {t('wrap_up.save_return')}
            </Button>
            {!canSave && (
              <p id="wrap-up-save-hint" className="sr-only">
                {t('wrap_up.save_hint')}
              </p>
            )}
          </div>
        </div>
      </Panel>
    </HighlightBox>
  );
}

/** Formats a duration in milliseconds as an operator-friendly MM:SS value. */
function formatDuration(ms: number): string {
  const sec = Math.floor(ms / 1000);
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}
